import { Button, IconButton, Loader, OdinIconSize } from '@myosh/odin-components';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../redux/hooks';
import {
  deleteSidePanelItem,
  JobStatus,
  removeSidePanelItemJob,
  selectActiveSidePanelItem,
  setSidePanelItemJob,
} from '../../redux/slices/side-panel-slice';
import { AiInsightFields } from './ai-insight-fields.component';
import { RecordInsightFields } from './record-insight-fields.component';

function SidePanelItem() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const activeItem = useSelector(selectActiveSidePanelItem);
  const insightItem = activeItem?.job?.status === JobStatus.Success ? activeItem.job.result : undefined;
  const error = activeItem?.job?.status === JobStatus.Failed ? activeItem.job.error : undefined;

  const isRecordInsight = activeItem?.type === 'RECORD_INSIGHT';
  const isLoading = activeItem?.job?.status === JobStatus.InProgress;

  const promptInputRef = useRef<HTMLTextAreaElement>(null);

  const handleClickGenerateAiInsight = () => {
    dispatch(
      setSidePanelItemJob({
        key: activeItem!.key,
        job: {
          id: undefined,
          status: undefined,
          extraPrompt: promptInputRef.current?.value,
          error: undefined,
          result: undefined,
          startedDate: undefined,
          completedDate: undefined,
        },
      })
    );
  };

  const handleClickGenerateRecordInsight = () => {
    dispatch(
      removeSidePanelItemJob({
        key: activeItem!.key,
      })
    );
  };

  const handleCloseActiveItem = () => {
    dispatch(deleteSidePanelItem(activeItem!.key));
  };

  const handleChangePrompt = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    dispatch(
      setSidePanelItemJob({
        key: activeItem!.key,
        job: {
          extraPrompt: e.target.value,
        },
      })
    );
  };

  return (
    <div className="flex h-full w-full flex-col">
      <div className="sticky bg-white p-2 border-b border-gray-4">
        <div className="flex justify-between items-center mb-2">
          <p className="text-xl font-bold">{isRecordInsight ? t('injuryRecordInsight') : t('widgetAiInsight')}</p>
          <IconButton icon="X" iconSize={OdinIconSize.Medium} onClick={handleCloseActiveItem} />
        </div>
        <p className="text-lg font-semibold">{activeItem?.title}</p>
      </div>

      <div className="flex-1 overflow-y-auto p-2 flex flex-col gap-2">
        {!isRecordInsight && (
          <>
            <div className="min-h-32 flex bg-[#fff]">
              <textarea
                ref={promptInputRef}
                name="custom-instructions"
                placeholder={t('aiInsightPromptPlaceholder')}
                className="h-full w-full resize-none appearance-none border border-gray-4 px-2 py-1 text-sm focus:outline-hidden"
                cols={30}
                rows={5}
                disabled={isLoading}
                value={activeItem?.job?.extraPrompt || ''}
                onChange={handleChangePrompt}
              />
            </div>

            <Button
              onClick={handleClickGenerateAiInsight}
              type={'primary'}
              fullWidth={true}
              htmlType="button"
              disabled={isLoading}
            >
              {t('generateAiInsight')}
            </Button>
          </>
        )}
        {isLoading && (
          <div className="flex flex-col items-center justify-center gap-2 flex-1">
            <Loader />
            <p>{isRecordInsight ? t('generatingRecordInsight') : t('generatingAiInsight')}...</p>
          </div>
        )}
        {error && (
          <p>
            {t('errorLabel')}: {error}
          </p>
        )}
        {isRecordInsight && error && (
          <Button
            onClick={handleClickGenerateRecordInsight}
            type={'primary'}
            fullWidth={true}
            htmlType="button"
            disabled={isLoading}
          >
            {t('retryGeneratingRecordInsight')}
          </Button>
        )}
        {isRecordInsight ? (
          <RecordInsightFields insightItem={insightItem} />
        ) : (
          <AiInsightFields insightItem={insightItem} />
        )}
      </div>
    </div>
  );
}

export default SidePanelItem;
